import { FormEvent, useState } from 'react'
import { Page, PageHeader } from '@/components/page'
import { router } from '@inertiajs/react'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { firstError } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { ArrowRight, Check, Users, Landmark } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'
import type { WizardFormationRef } from '@/types'

type Person = {
  id: string
  principal_kind: 'individual' | 'entity'
  full_name: string
  legal_name: string
  ownership_holder: boolean
}

type LlcStructure = {
  management_structure: 'member_managed' | 'manager_managed'
  owners: { principal_id: string; ownership_percentage: string }[]
  manager_principal_ids: string[]
}

type CorporationStructure = {
  authorized_common_shares: string
  par_value_policy: 'no_par_value' | 'par_value'
  par_value: string | null
  employee_pool_basis_points: number
  vesting_months: number
  cliff_months: number
  vesting_start_policy: 'incorporation_date' | 'custom_date'
  vesting_start_date: string | null
  acceleration_policy: 'double_trigger' | 'single_trigger' | 'none'
  consideration_type: 'ip' | 'nominal' | 'cash'
  aggregate_purchase_price: string
  shareholders: { principal_id: string; shares: string }[]
  director_principal_ids: string[]
  president_principal_id: string | null
  secretary_principal_id: string | null
  treasurer_principal_id: string | null
}

type Props = {
  formation: WizardFormationRef & { entity_suffix: string }
  people: Person[]
  structure: LlcStructure | CorporationStructure | null
  treasurer_required: boolean
}

function displayName(p: Person) {
  return p.full_name || p.legal_name
}

export default function StructuresEdit({ formation, people, structure, treasurer_required: treasurerRequired }: Props) {
  const basePath = useBasePath()
  const isCorporation = formation.entity_suffix === 'Inc.'

  const owners = people.filter((p) => p.ownership_holder)
  const individuals = people.filter((p) => p.principal_kind === 'individual')

  const llc = !isCorporation ? (structure as LlcStructure | null) : null
  const corp = isCorporation ? (structure as CorporationStructure | null) : null

  // LLC state
  const [managementStructure, setManagementStructure] = useState<string>(llc?.management_structure ?? 'member_managed')
  const [ownerPcts, setOwnerPcts] = useState<Record<string, string>>(() =>
    Object.fromEntries((llc?.owners ?? []).map((o) => [o.principal_id, o.ownership_percentage]))
  )
  const [managerIds, setManagerIds] = useState<string[]>(llc?.manager_principal_ids ?? [])

  // Corporation state
  const [authorizedShares, setAuthorizedShares] = useState(corp?.authorized_common_shares ?? '10000000')
  const [parValuePolicy, setParValuePolicy] = useState<'no_par_value' | 'par_value'>(corp?.par_value_policy ?? 'par_value')
  const [parValue, setParValue] = useState(corp?.par_value ?? '0.00001')
  const [poolPct, setPoolPct] = useState(corp ? String(corp.employee_pool_basis_points / 100) : '10')
  const [shareAllocations, setShareAllocations] = useState<Record<string, string>>(() =>
    corp
      ? Object.fromEntries(corp.shareholders.map((s) => [s.principal_id, s.shares]))
      : owners.length === 1 ? { [owners[0].id]: '9000000' } : {}
  )
  const [vestingMonths, setVestingMonths] = useState(corp ? String(corp.vesting_months) : '48')
  const [cliffMonths, setCliffMonths] = useState(corp ? String(corp.cliff_months) : '12')
  const [vestingStartPolicy, setVestingStartPolicy] = useState<'incorporation_date' | 'custom_date'>(corp?.vesting_start_policy ?? 'incorporation_date')
  const [vestingStartDate, setVestingStartDate] = useState(corp?.vesting_start_date ?? '')
  const [acceleration, setAcceleration] = useState<string>(corp?.acceleration_policy ?? 'double_trigger')
  const [consideration, setConsideration] = useState<string>(corp?.consideration_type ?? 'ip')
  const [purchasePrice, setPurchasePrice] = useState(corp?.aggregate_purchase_price ?? '100')
  const [directorIds, setDirectorIds] = useState<string[]>(corp?.director_principal_ids ?? [])
  const [presidentId, setPresidentId] = useState(corp?.president_principal_id ?? '')
  const [secretaryId, setSecretaryId] = useState(corp?.secretary_principal_id ?? '')
  const [treasurerId, setTreasurerId] = useState(corp?.treasurer_principal_id ?? '')

  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const err = (key: string) => errors[key]

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((id) => id !== value) : [...list, value]

  const llcTotal = owners.reduce((sum, o) => sum + (Number(ownerPcts[o.id]) || 0), 0)

  const authorized = Number(authorizedShares) || 0
  const poolShares = Math.floor((authorized * (Number(poolPct) || 0)) / 100)
  const founderCapacity = authorized - poolShares
  const allocatedShares = owners.reduce((sum, o) => sum + (Number(shareAllocations[o.id]) || 0), 0)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setErrors({})

    const payload = isCorporation
      ? {
          authorized_common_shares: authorizedShares,
          par_value_policy: parValuePolicy,
          par_value: parValuePolicy === 'par_value' ? parValue : undefined,
          employee_pool_basis_points: String(Math.round((Number(poolPct) || 0) * 100)),
          vesting_months: vestingMonths,
          cliff_months: cliffMonths,
          vesting_start_policy: vestingStartPolicy,
          vesting_start_date: vestingStartPolicy === 'custom_date' ? vestingStartDate : undefined,
          acceleration_policy: acceleration,
          consideration_type: consideration,
          aggregate_purchase_price: purchasePrice,
          shareholders: owners.map((o) => ({ principal_id: o.id, shares: shareAllocations[o.id] ?? '' })),
          director_principal_ids: directorIds,
          president_principal_id: presidentId,
          secretary_principal_id: secretaryId,
          treasurer_principal_id: treasurerId,
        }
      : {
          management_structure: managementStructure,
          owners: owners.map((o) => ({ principal_id: o.id, ownership_percentage: ownerPcts[o.id] ?? '' })),
          manager_principal_ids: managerIds,
        }

    router.patch(`${basePath}/formations/${formation.id}/wizard/structure`, payload, {
      onError: (serverErrors) => {
        const normalized: Record<string, string> = {}
        for (const [key, value] of Object.entries(serverErrors)) {
          normalized[key] = firstError(value) ?? ''
        }
        setErrors(normalized)
      },
      onFinish: () => setSubmitting(false),
    })
  }

  const individualOptions = individuals.map((p) => ({ value: p.id, label: displayName(p) }))

  return (
    <WizardLayout
      title={isCorporation ? 'Ownership and governance' : 'Ownership and management'}
      currentStep="structure"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: isCorporation ? 'Ownership and governance' : 'Ownership and management' }]}
      guide={
        <WizardGuide title={isCorporation ? 'Ownership and governance' : 'Ownership and management'}>
          {isCorporation ? (
            <>
              <p>Corppy files the incorporation; you set the planned ownership and governance terms.</p>
              <p className="text-xs">
                Authorized shares, vesting, and the board you name here become your filing documents. Corppy handles the
                standard filing — you don't file anything separately.
              </p>
            </>
          ) : (
            <p>
              Split ownership however you like — the percentages just need to total 100%. Then say who manages the
              company day to day.
            </p>
          )}
        </WizardGuide>
      }
      tips={
        <Tip seed="structure-groups">
          {isCorporation
            ? 'These are your planned terms, not a live cap table — actual shares are issued after your filing is accepted.'
            : 'Member percentages must add up to exactly 100%.'}
        </Tip>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <Page>
          <PageHeader
            title={isCorporation ? <>Ownership and governance</> : <>Ownership and management</>}
            subtitle={
              isCorporation
                ? <>Set your planned shares, equity terms, and the people who will govern the company.</>
                : <>Split ownership and choose how the company will be managed.</>
            }
          />

          {isCorporation ? (
            <>
              <section className="space-y-4" aria-labelledby="structure-capital">
                <h2 id="structure-capital" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Shares and capital
                </h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="authorized_common_shares">Authorized common shares</Label>
                    <Input
                      id="authorized_common_shares"
                      type="number"
                      min={1}
                      value={authorizedShares}
                      onChange={(e) => setAuthorizedShares(e.target.value)}
                      aria-invalid={!!err('authorized_common_shares')}
                    />
                    {err('authorized_common_shares') && <p className="text-sm text-destructive" role="alert">{err('authorized_common_shares')}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Par value</Label>
                    <RadioGroup
                      value={parValuePolicy}
                      onValueChange={(v) => setParValuePolicy(v as 'no_par_value' | 'par_value')}
                      className="gap-2"
                      aria-label="Par value"
                      aria-invalid={!!err('par_value_policy')}
                    >
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="no_par_value" id="par-none" />
                        No par value
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="par_value" id="par-set" />
                        Set a par value
                      </label>
                    </RadioGroup>
                    {parValuePolicy === 'par_value' && (
                      <Input
                        id="par_value"
                        type="number"
                        step="any"
                        min={0}
                        value={parValue}
                        onChange={(e) => setParValue(e.target.value)}
                        placeholder="e.g. 0.00001"
                        aria-label="Par value per share"
                        aria-invalid={!!err('par_value')}
                        className="mt-1"
                      />
                    )}
                    {err('par_value_policy') && <p className="text-sm text-destructive" role="alert">{err('par_value_policy')}</p>}
                    {err('par_value') && <p className="text-sm text-destructive" role="alert">{err('par_value')}</p>}
                  </div>
                </div>
              </section>

              <section className="space-y-4" aria-labelledby="structure-ownership">
                <h2 id="structure-ownership" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Founder shares
                </h2>
                <div className="space-y-3">
                  {owners.map((owner) => (
                    <div key={owner.id} className="flex items-end gap-4">
                      <div className="flex-1 space-y-1.5">
                        <Label htmlFor={`shares-${owner.id}`}>{displayName(owner)}</Label>
                        <Input
                          id={`shares-${owner.id}`}
                          type="number"
                          min={1}
                          value={shareAllocations[owner.id] ?? ''}
                          onChange={(e) => setShareAllocations((prev) => ({ ...prev, [owner.id]: e.target.value }))}
                          placeholder="Shares"
                        />
                      </div>
                    </div>
                  ))}
                  {err('shareholders') && <p className="text-sm text-destructive" role="alert">{err('shareholders')}</p>}
                  <p className="text-xs text-muted-foreground">
                    Allocated {allocatedShares.toLocaleString()} of {founderCapacity.toLocaleString()} shares available after the
                    employee pool.
                  </p>
                </div>

                <div className="space-y-1.5 max-w-[180px]">
                  <Label htmlFor="employee_pool">Employee pool</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="employee_pool"
                      type="number"
                      min={0}
                      max={100}
                      value={poolPct}
                      onChange={(e) => setPoolPct(e.target.value)}
                      aria-invalid={!!err('employee_pool_basis_points')}
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                  {err('employee_pool_basis_points') && (
                    <p className="text-sm text-destructive" role="alert">{err('employee_pool_basis_points')}</p>
                  )}
                </div>
              </section>

              <section className="space-y-4" aria-labelledby="structure-equity">
                <h2 id="structure-equity" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Equity terms
                </h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="vesting_months">Vesting period (months)</Label>
                    <Input id="vesting_months" type="number" min={1} value={vestingMonths} onChange={(e) => setVestingMonths(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cliff_months">Cliff (months)</Label>
                    <Input
                      id="cliff_months"
                      type="number"
                      min={1}
                      value={cliffMonths}
                      onChange={(e) => setCliffMonths(e.target.value)}
                      aria-invalid={!!err('cliff_months')}
                    />
                    {err('cliff_months') && <p className="text-sm text-destructive" role="alert">{err('cliff_months')}</p>}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Vesting starts</Label>
                  <RadioGroup
                    value={vestingStartPolicy}
                    onValueChange={(v) => setVestingStartPolicy(v as 'incorporation_date' | 'custom_date')}
                    className="gap-2"
                    aria-label="Vesting starts"
                  >
                    <label className="flex items-center gap-2 text-sm">
                      <RadioGroupItem value="incorporation_date" id="vest-incorporation" />
                      On the incorporation date
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <RadioGroupItem value="custom_date" id="vest-custom" />
                      A specific date
                    </label>
                  </RadioGroup>
                  {vestingStartPolicy === 'custom_date' && (
                    <Input
                      id="vesting_start_date"
                      type="date"
                      value={vestingStartDate}
                      onChange={(e) => setVestingStartDate(e.target.value)}
                      aria-invalid={!!err('vesting_start_date')}
                      aria-label="Vesting start date"
                      className="max-w-[200px]"
                    />
                  )}
                  {err('vesting_start_date') && <p className="text-sm text-destructive" role="alert">{err('vesting_start_date')}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label>Acceleration</Label>
                  <RadioGroup value={acceleration} onValueChange={setAcceleration} className="gap-2" aria-label="Acceleration">
                    <label className="flex items-center gap-2 text-sm">
                      <RadioGroupItem value="double_trigger" id="accel-double" />
                      Double-trigger
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <RadioGroupItem value="single_trigger" id="accel-single" />
                      Single-trigger
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <RadioGroupItem value="none" id="accel-none" />
                      None
                    </label>
                  </RadioGroup>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>What founder shares are purchased with</Label>
                    <RadioGroup value={consideration} onValueChange={setConsideration} className="gap-2" aria-label="What founder shares are purchased with">
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="ip" id="cons-ip" />
                        Contributed intellectual property
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="nominal" id="cons-nominal" />
                        Nominal cash
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="cash" id="cons-cash" />
                        Cash
                      </label>
                    </RadioGroup>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="aggregate_purchase_price">Aggregate purchase price ($)</Label>
                    <Input
                      id="aggregate_purchase_price"
                      type="number"
                      min={0}
                      step="any"
                      value={purchasePrice}
                      onChange={(e) => setPurchasePrice(e.target.value)}
                      aria-invalid={!!err('aggregate_purchase_price')}
                    />
                    {err('aggregate_purchase_price') && (
                      <p className="text-sm text-destructive" role="alert">{err('aggregate_purchase_price')}</p>
                    )}
                  </div>
                </div>
              </section>

              <section className="space-y-4" aria-labelledby="structure-governance">
                <h2 id="structure-governance" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Directors and officers
                </h2>

                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium text-foreground">Directors</legend>
                  {individuals.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Add a founder on the previous step to appoint directors.</p>
                  ) : (
                    <div className="space-y-2">
                      {individuals.map((p) => (
                        <label key={p.id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            id={`director-${p.id}`}
                            checked={directorIds.includes(p.id)}
                            onCheckedChange={() => setDirectorIds((prev) => toggle(prev, p.id))}
                          />
                          {displayName(p)}
                        </label>
                      ))}
                    </div>
                  )}
                  {err('directors') && <p className="text-sm text-destructive" role="alert">{err('directors')}</p>}
                </fieldset>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="president">President</Label>
                    <Select value={presidentId} onValueChange={setPresidentId}>
                      <SelectTrigger id="president" className="w-full" aria-invalid={!!err('president')}>
                        <SelectValue placeholder="Choose a president" />
                      </SelectTrigger>
                      <SelectContent>
                        {individualOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {err('president') && <p className="text-sm text-destructive" role="alert">{err('president')}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="secretary">Secretary</Label>
                    <Select value={secretaryId} onValueChange={setSecretaryId}>
                      <SelectTrigger id="secretary" className="w-full" aria-invalid={!!err('secretary')}>
                        <SelectValue placeholder="Choose a secretary" />
                      </SelectTrigger>
                      <SelectContent>
                        {individualOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {err('secretary') && <p className="text-sm text-destructive" role="alert">{err('secretary')}</p>}
                  </div>
                </div>
                {(treasurerRequired || treasurerId) && (
                  <div className="max-w-md space-y-1.5">
                    <Label htmlFor="treasurer">Treasurer</Label>
                    <Select value={treasurerId} onValueChange={setTreasurerId}>
                      <SelectTrigger id="treasurer" className="w-full" aria-invalid={!!err('treasurer')}>
                        <SelectValue placeholder="Choose a treasurer" />
                      </SelectTrigger>
                      <SelectContent>
                        {individualOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {err('treasurer') && <p className="text-sm text-destructive" role="alert">{err('treasurer')}</p>}
                  </div>
                )}
              </section>
            </>
          ) : (
            <>
              <section className="space-y-4" aria-labelledby="structure-llc-ownership">
                <h2 id="structure-llc-ownership" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Ownership
                </h2>
                <div className="space-y-3">
                  {owners.map((owner) => (
                    <div key={owner.id} className="flex items-end gap-4">
                      <div className="flex-1 space-y-1.5">
                        <Label htmlFor={`pct-${owner.id}`}>{displayName(owner)}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id={`pct-${owner.id}`}
                            type="number"
                            min={0}
                            max={100}
                            value={ownerPcts[owner.id] ?? ''}
                            onChange={(e) => setOwnerPcts((prev) => ({ ...prev, [owner.id]: e.target.value }))}
                            placeholder="0"
                          />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {err('owners') && <p className="text-sm text-destructive" role="alert">{err('owners')}</p>}
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    Total: {llcTotal}%
                    {llcTotal === 100 ? (
                      <span className="inline-flex items-center gap-1 text-foreground">
                        <Check className="size-3.5" aria-hidden="true" />
                        Ownership is complete
                      </span>
                    ) : (
                      <span>— must equal 100%</span>
                    )}
                  </p>
                </div>
              </section>

              <section className="space-y-4" aria-labelledby="structure-llc-management">
                <h2 id="structure-llc-management" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Management
                </h2>
                <RadioGroup
                  value={managementStructure}
                  onValueChange={setManagementStructure}
                  aria-labelledby="structure-llc-management"
                  aria-invalid={!!err('management_structure')}
                  className="grid gap-3 sm:grid-cols-2"
                >
                  <label
                    htmlFor="mgmt-member"
                    className="group flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted hover:border-foreground/40"
                  >
                    <RadioGroupItem value="member_managed" id="mgmt-member" className="mt-1" />
                    <span className="flex flex-col gap-1">
                      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Users className="size-4 text-muted-foreground" /> Member-managed
                      </span>
                      <span className="text-xs text-muted-foreground">The owners run the company directly.</span>
                    </span>
                  </label>
                  <label
                    htmlFor="mgmt-manager"
                    className="group flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted hover:border-foreground/40"
                  >
                    <RadioGroupItem value="manager_managed" id="mgmt-manager" className="mt-1" />
                    <span className="flex flex-col gap-1">
                      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Landmark className="size-4 text-muted-foreground" /> Manager-managed
                      </span>
                      <span className="text-xs text-muted-foreground">One or more managers run the day to day.</span>
                    </span>
                  </label>
                </RadioGroup>
                {err('management_structure') && <p className="text-sm text-destructive" role="alert">{err('management_structure')}</p>}

                {managementStructure === 'manager_managed' && (
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium text-foreground">Managers</legend>
                    {people.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Add a founder on the previous step to name managers.</p>
                    ) : (
                      <div className="space-y-2">
                        {people.map((p) => (
                          <label key={p.id} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={managerIds.includes(p.id)}
                              onCheckedChange={() => setManagerIds((prev) => toggle(prev, p.id))}
                            />
                            {displayName(p)}
                          </label>
                        ))}
                      </div>
                    )}
                    {err('managers') && <p className="text-sm text-destructive" role="alert">{err('managers')}</p>}
                  </fieldset>
                )}
              </section>
            </>
          )}

          <WizardActions currentStep="structure" formationId={formation.id} disabled={submitting}>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Continue'}
              <ArrowRight className="size-4" />
            </Button>
          </WizardActions>
        </Page>
      </form>
    </WizardLayout>
  )
}
